// Elon University: public college page (#2652, round 2), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Elon's Common Data Set 2025-2026 (ElonCDS2025-2026Final.xlsx), linked
//    from the Office of Institutional Effectiveness's Common Data Sets page
//    on elon.edu (the file itself sits on Elon's CDN, eloncdn.blob.core.
//    windows.net, so the source link is the elon.edu page): C1 applicants,
//    admits and enrollees for Fall 2025 with the residency table, C8 test
//    policy for Fall 2027, C21 early decision counts for the Fall 2025
//    entering class (C22 gives early action dates but no counts).
//  - elon.edu admissions: Apply (application deadlines and the test-optional
//    line, which names students enrolling in fall 2027), Dates & Deadlines
//    (Early Decision II, the binding terms, program deadlines), and the
//    Test-Optional FAQ. Quirk: the Dates & Deadlines page names no entry
//    year and some of its notes still carry 2026 dates (Regular Deadline
//    notification "March 20, 2026"); the dates here are the ones without a
//    year, which match the Apply page for fall 2027.
//  - Majors, Minors & Programs (elon.edu): the audition, portfolio or
//    interview footnote for the performing arts and the B.F.A. in Art.
//  - Elon Financial Aid, Tuition: the 2026-2027 cost of attendance for newly
//    admitted students, item by item. The CDS G1 gives the same tuition and
//    food, but $8,898 for housing (the aid office uses a first-year average
//    of $9,306) and G5 gives no transportation for residents, so the aid
//    office's budget is used here.
//  - College Scorecard API, school id 198516 (net price by family income).
// Departures: the CDS C8 marks the SAT or ACT "required for some"; the
// admissions pages say what that means (Nursing and homeschooled
// applicants). The CDS C21 dates are garbled (a notification date of
// November 1), so the plan dates come from the admissions pages only.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Elon Common Data Set 2025-2026',
        url: 'https://www.elon.edu/u/administration/institutional-effectiveness/cds/',
        read: '2026-10-01',
    },
    apply: {
        label: 'Elon Admissions, Apply',
        url: 'https://www.elon.edu/u/admissions/undergraduate/apply/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Elon Admissions, Dates & Deadlines',
        url: 'https://www.elon.edu/u/admissions/undergraduate/apply/dates-and-deadlines/',
        read: '2026-10-01',
    },
    testing: {
        label: 'Elon Admissions, Test-Optional Frequently Asked Questions',
        url: 'https://www.elon.edu/u/admissions/undergraduate/apply/test-optional-frequently-asked-questions/',
        read: '2026-10-01',
    },
    majorsPage: {
        label: 'Elon, Majors, Minors & Programs',
        url: 'https://www.elon.edu/u/academics/majors-minors-programs/',
        read: '2026-10-01',
    },
    cost: {
        label: 'Elon Financial Aid, Tuition and Cost of Attendance 2026-2027',
        url: 'https://www.elon.edu/u/admissions/undergraduate/financial-aid/tuition-and-aid/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?198516-Elon-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'elon-university',
    kbId: 'elon_university',
    name: 'Elon University',
    shortName: 'Elon',
    location: 'Elon, NC',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Elon color and type guide); decoration only.
    branding: {
        primary: '#73000A',
        secondary: '#B59A57',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/elon_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 18557,
        admitted: 11740,
        enrolled: 1333,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 4225, admitted: 2593 },
            { group: 'Out-of-state', applied: 13850, admitted: 8839 },
            { group: 'International', applied: 482, admitted: 308 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Elon\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early decision', applied: 353, admitted: 304 },
        ],
        note: 'The Common Data Set gives one early decision count for the fall 2025 entering class; it does not split Early Decision I from Early Decision II. It lists a nonbinding early action plan but gives no early action counts.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Elon as a whole. Some programs ask for more:',
        rows: [
            { school: 'Nursing (B.S.N.)', requirement: 'A direct-admit program: Nursing applicants must send SAT or ACT scores, with at least an 1100 SAT or a 22 ACT to be eligible.', source: 'testing' },
            { school: 'Acting, Dance Performance & Choreography, Music Performance, Music Production & Recording Arts, Music Theatre, Theatrical Design & Technology, Art (B.F.A.)', requirement: 'An audition, portfolio or interview is required.', source: 'majorsPage' },
            { school: 'Honors and Fellows programs', requirement: 'Apply to Elon by November 1 and to Honors or Fellows by November 10. Notification by April 1.', source: 'deadlines' },
            { school: 'Accelerated Pathways in PA and PT', requirement: 'A separate application by November 10.', source: 'deadlines' },
        ],
        source: 'deadlines',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Deadline', value: 'January 10' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'December 1', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'December 20', binding: false },
            { plan: 'Regular Deadline', deadline: 'January 10', decision: 'March 20', binding: false },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'February 10', binding: true },
        ],
        note: 'Enrollment deposits are due January 10 for Early Decision (non-refundable), March 1 for Early Decision II (non-refundable), and May 1 for Early Action and the Regular Deadline. Applications after January 10 are considered on a space-available basis. Spring first-year applicants apply by December 1.',
        source: 'apply',
        binding: 'Both Early Decision plans are binding: you may apply to only one school early decision, you and a parent or guardian sign an Early Decision Agreement, and if admitted you withdraw your applications to all other colleges. A non-refundable $1,000 enrollment deposit is due, and you cannot defer admission.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'For students enrolling in fall 2027, Elon is test-optional. Nursing (B.S.N.) applicants and homeschooled students must send SAT or ACT scores. You may self-report scores; if admitted, you send an official score before enrolling. You can ask Elon not to review scores it already has.',
        source: 'testing',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for some fall 2027 applicants, with scores due by December 14.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition (full time)', amount: 50386 },
            { item: 'Student activity fee', amount: 292 },
            { item: 'Health and wellness fee', amount: 690 },
            { item: 'Housing (first-year average)', amount: 9306 },
            { item: 'Food (basic meal plan)', amount: 8229 },
            { item: 'Books and supplies', amount: 1000 },
            { item: 'Transportation allowance', amount: 1500 },
            { item: 'Miscellaneous expense allowance', amount: 2000 },
        ],
        total: 73403,
        note: 'The cost of attendance for a newly admitted, full-time undergraduate, fall 2026 and spring 2027. Billed charges (tuition, fees, food and housing) come to $68,903.',
        source: 'cost',
        netPrice: {
            average: 35785,
            byIncome: [
                { income: '$0 to $30,000', amount: 20986 },
                { income: '$30,001 to $48,000', amount: 21272 },
                { income: '$48,001 to $75,000', amount: 21374 },
                { income: '$75,001 to $110,000', amount: 30529 },
                { income: 'Over $110,000', amount: 46478 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Elon\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
